import type { useGamePassSummary } from '@/components/dashboard/game-pass-card'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'

type FundingSource = 'credits' | 'game-pass'

export function GamePassFundingChoice({
  summary,
  source,
  onChange,
  freeMode,
  excluded,
  roomPolicy,
  disabled = false,
}: {
  summary: ReturnType<typeof useGamePassSummary>
  source: FundingSource | null
  onChange: (source: FundingSource) => void
  freeMode: boolean
  excluded: 'comparison' | 'byom' | null
  roomPolicy?: 'split' | 'host'
  disabled?: boolean
}) {
  const { t, language } = useI18n()
  return (
    <div className="space-y-2 border-t pt-4 text-sm" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid={roomPolicy ? 'game-pass-room-payment' : 'game-pass-launcher'}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{t(roomPolicy ? 'gamePasses.payment' : 'gamePasses.title')}</span>
        {summary.data && <span className="tabular-nums" data-testid={roomPolicy ? 'game-pass-room-balance' : 'game-pass-launcher-balance'}>{t('gamePasses.available', { count: summary.data.remaining })}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{t('gamePasses.shared')}</p>
      {!summary.data && !summary.isError && <p role="status" className="text-xs text-muted-foreground">{t('gamePasses.loading')}</p>}
      {summary.isError && (
        <div className="space-y-2">
          <p role="alert" className="text-xs text-destructive">{t('gamePasses.loadError')}</p>
          <Button type="button" size="sm" variant="outline" onClick={() => summary.refetch()}>{t('gamePasses.retry')}</Button>
        </div>
      )}
      {excluded ? (
        <p className="text-xs text-muted-foreground" data-testid="game-pass-excluded">{t(`gamePasses.excluded.${excluded}`)}</p>
      ) : freeMode ? (
        <p className="text-xs text-muted-foreground" data-testid="game-pass-free-mode">{t('gamePasses.freeMode')}</p>
      ) : (
        <>
          <fieldset className="space-y-1" data-testid="game-pass-funding-choice">
            <legend className="sr-only">{t('gamePasses.payment')}</legend>
            {(['game-pass', 'credits'] as const).map(option => (
              <label key={option} className="flex min-h-11 cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name={roomPolicy ? 'game-pass-room-funding' : 'game-pass-funding'}
                  disabled={disabled}
                  value={option}
                  checked={source === option}
                  onChange={() => onChange(option)}
                  onClick={() => {
                    if (!summary.data && source === option)
                      onChange(option)
                  }}
                  data-testid={option === 'game-pass' ? 'game-pass-funding-pass' : 'game-pass-funding-credits'}
                  className="size-4 shrink-0 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                />
                <span>{t(option === 'game-pass' ? 'gamePasses.usePass' : 'gamePasses.useCredits')}</span>
              </label>
            ))}
          </fieldset>
          {source !== null && <p className="text-xs text-muted-foreground" data-testid="game-pass-selected-funding">{t(source === 'game-pass' ? roomPolicy === 'split' ? 'gamePasses.room.splitPass' : roomPolicy === 'host' ? 'gamePasses.room.hostPass' : 'gamePasses.selectedPass' : 'gamePasses.selectedCredits')}</p>}
          {source === 'game-pass' && summary.data?.remaining === 0 && <p role="status" className="text-xs text-muted-foreground">{t('gamePasses.errors.game_pass_unavailable')}</p>}
        </>
      )}
      {roomPolicy && <p className="text-xs text-muted-foreground">{t('gamePasses.room.usedAtStart')}</p>}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer py-1">{t('gamePasses.scope')}</summary>
        <p className="mt-2 max-w-prose leading-relaxed">{t('gamePasses.details')}</p>
      </details>
    </div>
  )
}
